import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { navigationMenu } from '../data/mockData';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  // Separate regular menu items from Internet Subesi
  const regularMenuItems = navigationMenu.filter(item => item.name !== 'İNTERNET ŞUBESİ');
  const internetSubesi = navigationMenu.find(item => item.name === 'İNTERNET ŞUBESİ');

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-[#3d4654]/95 backdrop-blur-md shadow-lg' : 'bg-[#3d4654]'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Smaller */}
          <Link to="/" className="flex items-center group">
            <img 
              src="https://customer-assets.emergentagent.com/job_site-mirror-163/artifacts/mi437ith_logooo.png" 
              alt="TMD Logo" 
              className="h-8 w-auto transform group-hover:scale-105 transition-transform"
            />
          </Link>

          {/* Desktop Navigation - Center */}
          <nav className="hidden lg:flex items-center space-x-6 flex-1 justify-center">
            {regularMenuItems.map((item) => (
              <div key={item.name} className="relative group">
                {item.hasDropdown ? (
                  <>
                    <button
                      onClick={() => toggleDropdown(item.name)}
                      className="flex items-center space-x-1 px-3 py-2 text-white/90 hover:text-orange-400 font-normal text-sm transition-colors uppercase"
                    >
                      <span>{item.name}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    {openDropdown === item.name && (
                      <div className="absolute top-full left-0 mt-2 w-64 bg-[#3d4654] rounded-lg shadow-xl border border-slate-600 overflow-hidden">
                        {item.items.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.link}
                            onClick={() => setOpenDropdown(null)}
                            className="block px-4 py-3 text-white/80 hover:bg-orange-500 hover:text-white transition-colors text-sm"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.link}
                    className="px-3 py-2 text-white/90 hover:text-orange-400 font-normal text-sm transition-colors uppercase"
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Internet Subesi Button - Large Orange Button */}
          {internetSubesi && (
            <a
              href={internetSubesi.link}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:block"
            >
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-8 py-4 rounded-lg hover:shadow-lg hover:shadow-orange-500/50 transition-all">
                <span className="text-white font-semibold text-sm uppercase tracking-wide">
                  İNTERNET ŞUBESİ
                </span>
              </div>
            </a>
          )}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-white p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#3d4654] rounded-b-lg shadow-xl border-t border-slate-600">
            {/* Internet Subesi Button - Mobile */}
            {internetSubesi && (
              <div className="p-4 border-b border-slate-600">
                <a
                  href={internetSubesi.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-3 rounded-lg text-center hover:shadow-lg transition-all"
                >
                  <span className="text-white font-semibold text-sm uppercase tracking-wide">
                    İNTERNET ŞUBESİ
                  </span>
                </a>
              </div>
            )}
            
            {navigationMenu.map((item) => (
              item.name !== 'İNTERNET ŞUBESİ' && (
                <div key={item.name} className="border-b border-slate-600 last:border-b-0">
                  {item.hasDropdown ? (
                    <>
                      <button
                        onClick={() => toggleDropdown(item.name)}
                        className="w-full flex items-center justify-between px-4 py-3 text-white font-medium hover:bg-slate-700 transition-colors"
                      >
                        <span>{item.name}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${
                          openDropdown === item.name ? 'rotate-180' : ''
                        }`} />
                      </button>
                      {openDropdown === item.name && (
                        <div className="bg-slate-900">
                          {item.items.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.link}
                              onClick={() => {
                                setMobileMenuOpen(false);
                                setOpenDropdown(null);
                              }}
                              className="block px-8 py-2 text-white/80 hover:bg-orange-500 hover:text-white transition-colors text-sm"
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {item.external ? (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-4 py-3 text-white font-medium hover:bg-orange-500 transition-colors"
                        >
                          {item.name}
                        </a>
                      ) : (
                        <Link
                          to={item.link}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-4 py-3 text-white font-medium hover:bg-slate-700 transition-colors"
                        >
                          {item.name}
                        </Link>
                      )}
                    </>
                  )}
                </div>
              )
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
